import { Fragment } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Building2 } from '@/components/icons'
import { TopBarBackButton } from '@/components/topbar/TopBarBackButton'
import { AppLayout } from '@/components/AppLayout'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import { MenuItem } from '@/components/ui/menu-item'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Skeleton } from '@/components/ui/skeleton'
import { useCompanyListMine } from '@/lib/hooks/company'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { setActiveCompanyId } from '@/lib/company-context'

export const Route = createFileRoute('/_auth/profile/company')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const queryClient = useQueryClient()
  const { data: companies, isPending } = useCompanyListMine()
  const { data: currentUser } = useCurrentCrewMember()

  const currentCompanyId = currentUser?.companyId

  const handleSelect = async (companyId: string) => {
    if (companyId === currentCompanyId) return
    // Update the persisted id + module cache first so every refetch triggered
    // by the cache clear already carries the new x-company-id header.
    await setActiveCompanyId(companyId)
    queryClient.clear()
    navigate({ to: '/home' })
  }

  const body = (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-sm font-semibold text-content-primary-bold mb-4 px-1">
          Your Companies
        </p>
        {isPending ? (
          <Card className="flex flex-col gap-2 w-full px-2 py-2">
            {Array.from({ length: 2 }).map((_, index) => (
              <Fragment key={index}>
                {index > 0 && <hr className="border-border-L1" />}
                <div className="flex items-center gap-3 px-2 py-3">
                  <Skeleton className="size-5 rounded-md shrink-0" />
                  <Skeleton className="h-5 w-40" />
                </div>
              </Fragment>
            ))}
          </Card>
        ) : (
          <Card className="flex flex-col gap-2 w-full px-2">
            <RadioGroup
              value={currentCompanyId ?? ''}
              onValueChange={(value) => void handleSelect(value)}
              className="flex flex-col gap-2"
            >
              {(companies ?? []).map((membership, index) => (
                <Fragment key={membership.companyId}>
                  {index > 0 && <hr className="border-border-L1" />}
                  <MenuItem
                    leftIcon={
                      <Building2 className="size-5 text-content-primary-bold" />
                    }
                    rightIcon={<RadioGroupItem value={membership.companyId} />}
                    size="large"
                    onClick={() => void handleSelect(membership.companyId)}
                  >
                    {membership.companyName}
                  </MenuItem>
                </Fragment>
              ))}
            </RadioGroup>
          </Card>
        )}
      </div>
    </div>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Change Company"
          breadcrumb={
            <span>
              <Link to="/profile" className="hover:underline">
                Profile
              </Link>
              <span className="text-content-primary-subtle">
                {' '}
                / Change Company
              </span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Change Company" />}>
      {body}
    </AppLayout>
  )
}
